// ./application-steps/StepContent3.js

import React, { memo } from "react";
import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";

import FormControlLabel from "@mui/material/FormControlLabel";
import { useGetAvailableAwardsQuery } from "../../../services/gen-app";

function StepContent3({
  formData,
  setFormData,
  stepErrors,
  handleChange,
  isReadOnly,
  appID,
}) {
  const { data: availableAwardsData } = useGetAvailableAwardsQuery(appID, {
    skip: !appID,
  });

  const handleCheckboxChange = (awardId) => (event) => {
    const isChecked = event.target.checked;

    setFormData((prev) => {
      let updatedAwards;
      if (isChecked) {
        updatedAwards = [
          ...prev.selectedAwards,
          { subjectAwardId: awardId, moreInfo: "", grade11: "", grade12: "" },
        ];
      } else {
        updatedAwards = prev.selectedAwards.filter(
          (award) => award.subjectAwardId !== awardId
        );
      }

      return { ...prev, selectedAwards: updatedAwards };
    });
  };

  const handleMoreInfoChange = (awardId) => (event) => {
    const newMoreInfo = event.target.value;
    setFormData((prev) => ({
      ...prev,
      selectedAwards: prev.selectedAwards.map((award) =>
        award.subjectAwardId === awardId
          ? { ...award, moreInfo: newMoreInfo }
          : award
      ),
    }));
  };

  const handleGradeChange = (awardId, gradeField) => (event) => {
    const newVal = event.target.value;
    setFormData((prev) => ({
      ...prev,
      selectedAwards: prev.selectedAwards.map((award) =>
        award.subjectAwardId === awardId
          ? { ...award, [gradeField]: newVal }
          : award
      ),
    }));
  };

  const getStatus = (field, isRequired) => {
    if (stepErrors[field]) return "error";
    if (isRequired && formData[field]) return "success";
    if (!isRequired && !formData[field]) return "warning";
    return "";
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
      className="award-selection-form"
    >
      <form>
        {/* Header */}
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Typography variant="h5" fontWeight={800} textAlign="center">
            Award Selection
          </Typography>
          <Typography variant="h6" fontWeight={800} textAlign="center">
            SUBJECT BASED AWARDS
          </Typography>
        </Stack>

        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Click a subject to indicate interest in a subject-based award. For
            each selected subject, please enter the grade you received for Grade
            11 and/or Grade 12. If you cannot provide a grade, include any
            relevant details in the Additional Information box. At least one
            grade (Grade 11 or Grade 12) is required OR Additional Information
            if no grades are available.
          </Typography>
        </Alert>

        <Divider sx={{ my: 3 }} />

        {/* Award list */}
        <Stack spacing={2}>
          {availableAwardsData &&
            availableAwardsData.data.map((award, idx) => {
              const isChecked = formData.selectedAwards.some(
                (a) => a?.subjectAwardId === award?.id
              );
              const moreInfo =
                formData.selectedAwards.find(
                  (a) => a.subjectAwardId === award?.id
                )?.moreInfo || "";

              return (
                <Box key={award?.id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isChecked}
                        onChange={handleCheckboxChange(award?.id)}
                        name={`award_${award?.id}`}
                        color="primary"
                        disabled={isReadOnly}
                      />
                    }
                    label={
                      <Typography variant="body1">{award?.subject}</Typography>
                    }
                  />

                  {isChecked && (
                    <>
                      <Grid
                        container
                        spacing={2}
                        sx={{ ml: 0, mt: 1, alignItems: "flex-start" }}
                      >
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            label="Grade (Grade 11)"
                            value={
                              formData.selectedAwards.find(
                                (a) => a.subjectAwardId === award?.id
                              )?.grade11 || ""
                            }
                            onChange={handleGradeChange(award?.id, "grade11")}
                            variant="outlined"
                            size="small"
                            disabled={isReadOnly}
                            error={Boolean(stepErrors[`grade_${award.id}`])}
                            helperText={stepErrors[`grade_${award.id}`] || ""}
                            fullWidth
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            label="Grade (Grade 12)"
                            value={
                              formData.selectedAwards.find(
                                (a) => a.subjectAwardId === award?.id
                              )?.grade12 || ""
                            }
                            onChange={handleGradeChange(award?.id, "grade12")}
                            variant="outlined"
                            size="small"
                            disabled={isReadOnly}
                            // Reserve helper space when Grade 11 has an error so the fields remain top-aligned
                            helperText={
                              stepErrors[`grade_${award.id}`] ? " " : undefined
                            }
                            fullWidth
                          />
                        </Grid>
                      </Grid>

                      <TextField
                        label="Additional Information"
                        value={moreInfo}
                        onChange={handleMoreInfoChange(award?.id)}
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={2}
                        margin="dense"
                        disabled={isReadOnly}
                        error={Boolean(stepErrors[`moreInfo_${award.id}`])}
                        helperText={stepErrors[`moreInfo_${award.id}`] || ""}
                        sx={{
                          ml: 2, // indent under checkbox
                          mt: 0.5,
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: getStatus(
                                `moreInfo_${award.id}`,
                                true
                              ),
                            },
                            "&:hover fieldset": {
                              borderColor: getStatus(
                                `moreInfo_${award.id}`,
                                true
                              ),
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: getStatus(
                                `moreInfo_${award.id}`,
                                true
                              ),
                            },
                          },
                        }}
                      />
                    </>
                  )}

                  {idx < availableAwardsData.data.length - 1 && (
                    <Divider sx={{ mt: 2 }} />
                  )}
                </Box>
              );
            })}
        </Stack>
      </form>
    </Paper>
  );
}

export default memo(StepContent3);
